import { useMemo } from "react"

import { useAuthentication } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"

import { useQueryKey, useRenameQueryResult } from "../utils"
import { consultantWorkspaceNames } from "./sigma.types"
import type { SigmaPaginatedResponse, SigmaWorkbook } from "./sigma.types"
import { useSigmaApi } from "./useSigmaApi"

const placeholderWorkbook: SigmaWorkbook = {
  createdAt: "",
  createdBy: "",
  id: "",
  latestVersion: 1,
  name: "",
  ownerId: "",
  path: "",
  updatedAt: "",
  updatedBy: "",
  url: "",
  workbookId: "",
  workbookUrlId: "",
}

const getPlaceholderData = (
  placeholderCount: number
): SigmaPaginatedResponse<SigmaWorkbook> => {
  const placeholderWorkbooks = Array(placeholderCount)
    .fill(0)
    .map((workbook, index) => {
      const id = (index + 1).toString()

      return {
        ...placeholderWorkbook,
        ...({ id, name: id, workbookId: id } as SigmaWorkbook),
      }
    })

  return {
    entries: placeholderWorkbooks,
    hasMore: false,
    nextPage: null,
    total: placeholderWorkbooks.length,
  }
}

// Filtering is necessary because the user associated with the API secret
// has access to all org teams/workspaces
const filterWorkbooks = (
  workbooks: SigmaWorkbook[],
  workspaceNames: UseSigmaWorkbooksOptions["apiOptions"]["workspaceNames"]
): SigmaWorkbook[] => {
  const accessibleWorkbooks = workbooks.filter(
    (workbook) =>
      workbook.path === "" ||
      workspaceNames
        .map((name) => name.toLowerCase())
        .includes(workbook.path.toLowerCase())
  )

  return accessibleWorkbooks
}

// Sorts workbooks by name (case-insensitive)
const sortWorkbooks = (workbooks: SigmaWorkbook[]): SigmaWorkbook[] => {
  return [...workbooks].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  )
}

interface UseSigmaWorkbooksOptions<TData = unknown, TError = unknown> {
  apiOptions: {
    placeholderCount?: number
    workspaceNames: SigmaWorkbook["path"][]
  }
  queryOptions?: UseQueryOptions<TData, TError>
}

/**
 * Gets the Sigma workbooks for the requested workspaces
 *
 * @returns The paginated Sigma workbooks
 *
 * @example
 * const {
 *   sigmaWorkbooksData,
 *   sigmaWorkbooksIsPlaceholderData,
 * } = useSigmaWorkbooks({
 *   apiOptions: {
 *     workspaceNames: [organization?.name],
 *   }
 * })
 *
 * @example
 * const {
 *   sigmaWorkbooksData,
 *   sigmaWorkbooksIsPlaceholderData,
 * } = useSigmaWorkbooks(
 *   apiOptions: {
 *     workspaceNames: ["All Consultants"],
 *   }
 * })
 */
export const useSigmaWorkbooks = (
  options: UseSigmaWorkbooksOptions<SigmaPaginatedResponse<SigmaWorkbook>>
) => {
  const { isNZeroOSUser } = useAuthentication()
  const { fetchSigmaWorkbooks, sigmaAccessTokenIsFetched } = useSigmaApi()

  const { apiOptions, queryOptions = {} } = options
  const enabled =
    sigmaAccessTokenIsFetched && apiOptions.workspaceNames.length > 0

  const safePlaceholderCount = Number.isFinite(
    apiOptions.placeholderCount ?? NaN
  )
    ? (apiOptions.placeholderCount as unknown as number)
    : 3
  const placeholderData = useMemo(() => {
    return getPlaceholderData(safePlaceholderCount)
  }, [safePlaceholderCount])

  const queryKey = useQueryKey(
    ApiQueryName.SigmaWorkbooks,
    "getMany",
    null,
    apiOptions
  )
  // Filter out consultant workspace names if not an NZeroOS user
  const workspaceNames: string[] = [
    ...(isNZeroOSUser
      ? apiOptions.workspaceNames
      : apiOptions.workspaceNames.filter(
          (workspaceName) => !consultantWorkspaceNames.includes(workspaceName)
        )),
  ]

  const queryResult = useQuery({
    ...queryOptions,
    enabled,
    placeholderData,
    queryFn: fetchSigmaWorkbooks,
    queryKey,
    select: (data) => ({
      ...data,
      entries: sortWorkbooks(
        filterWorkbooks(data?.entries ?? [], workspaceNames)
      ),
    }),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.SigmaWorkbooks)
}
